/* eslint-disable @typescript-eslint/no-explicit-any */
import { IonButton, IonContent, IonIcon, IonModal } from "@ionic/react";
import { chevronBack, checkmark, close } from "ionicons/icons";
import React, { useState } from "react";
import { CoughImage, ClearImage } from "../../../assets/icons";
import { BiwazeEnum } from "../../../utils/constants";
import { STEP_IDS } from "../Onboarding";
import analytics from "../../../services/firebaseAnalytics";
interface DeviceUsageConfirmProps {
  first_name: string;
  currentStep: number;
  steps: any[];
  devices: any[];
  prevStep?: () => void;
  nextStep: (index?: number) => void;
  handleSkipSetup: () => void;
  handleComplete: () => void;
  usesBiWazeDevice: any | null;
  setUsesBiWazeDevice: (uses: BiwazeEnum) => void;
  hideSkip?: boolean;
  handleNavigateToStep?: (stepId: string) => void;
}

const DeviceUsageConfirm: React.FC<DeviceUsageConfirmProps> = ({
  usesBiWazeDevice,
  first_name,
  devices,
  prevStep,
  nextStep,
  handleSkipSetup,
  handleComplete,
  setUsesBiWazeDevice,
  handleNavigateToStep,
  hideSkip = false,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(() => {
    if (usesBiWazeDevice === BiwazeEnum.COUGH) {
      // Check if user already has cough device, auto select clear
      const hasCough = devices.some((device) =>
        device?.name?.toLowerCase().includes("cough")
      );
      return hasCough ? "clear" : "cough";
    }
    if (usesBiWazeDevice === BiwazeEnum.CLEAR) {
      return "clear";
    }
    return null;
  });
  const [hoveredOption, setHoveredOption] = useState<string | null>(null);
  const [showOverride, setShowOverride] = useState(false);
  const firstName =
    first_name.length < 16 ? first_name : first_name.slice(0, 16) + "....";

  const handleContinue = () => {
    if (!selectedOption) return;

    const deviceTypeMap: any = {
      cough: BiwazeEnum.COUGH,
      clear: BiwazeEnum.CLEAR,
    };

    const deviceType = deviceTypeMap[selectedOption];
    if (!deviceType) return;

    setUsesBiWazeDevice(deviceType);

    const hasExistingDevice = devices.some((device) =>
      device?.name?.toLowerCase().includes(selectedOption)
    );

    if (hasExistingDevice) {
      setShowOverride(true);
    } else {
      handleNavigateToStep?.(STEP_IDS.QR_SCAN);
    }
  };

  // Log analytics event when component mounts
  React.useEffect(() => {
    const logAnalyticsEvent = async () => {
      await analytics.logEvent("onboarding_device_usage_viewed", {});
    };
    logAnalyticsEvent();
  }, []);

  return (
    <IonContent className="ion-padding onboarding-section">
      <div className="navigation-header">
        {prevStep && (
          <button className="back-text-button" onClick={prevStep}>
            <IonIcon icon={chevronBack} className="back-icon" />
            <span>Back</span>
          </button>
        )}
        {!hideSkip ? (
          <button className="skip-setup-button" onClick={handleSkipSetup}>
            Skip Setup
          </button>
        ) : (
          <>
            <button className="skip-setup-button" onClick={handleSkipSetup}>
              Close
            </button>
          </>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4 py-6">
        <div className=" w-full gap-4">
          {/* Welcome Section */}
          <div className="text-center mb-4 flex flex-col gap-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              Hello, {firstName}
              {firstName ? "!" : ""}
            </h1>
            <h3
              style={{
                fontFamily: "'Figtree', sans-serif",
                fontStyle: "normal",
                fontWeight: 600,
                fontSize: "22px",
                lineHeight: "28px",
                textAlign: "center",
                color: "#000000",
                alignSelf: "stretch",
                flexGrow: 0,
              }}
            >
              Select your BiWaze<sup>®</sup> system to link your account
            </h3>
          </div>

          {/* Options */}
          <div className="space-y-6 mb-8">
            {[
              {
                key: "cough",
                title: (
                  <>
                    BiWaze<sup>®</sup> Cough
                  </>
                ),
                img: CoughImage,
                pulseClass: "pulse-orange",
                gradientClass:
                  "gradient-orange pattern-dots-orange border-orange-400 shadow-xl bg-white",
                hoverClass:
                  "bg-gradient-to-r from-white to-orange-50 border-orange-200 shadow-lg",
                isConnected:
                  devices.filter((device) =>
                    device?.type.toLowerCase().includes("cough")
                  ).length > 0,
                serialNumber:
                  devices.filter((device) =>
                    device?.type?.toLowerCase().includes("cough")
                  ).length > 0
                    ? devices.filter((device) =>
                        device?.type?.toLowerCase().includes("cough")
                      )[0].serialNumber
                    : "",
              },
              {
                key: "clear",
                title: (
                  <>
                    BiWaze<sup>®</sup> Clear
                  </>
                ),
                img: ClearImage,
                pulseClass: "pulse-blue",
                gradientClass:
                  "gradient-blue pattern-dots-blue border-blue-400 shadow-xl bg-white",
                hoverClass:
                  "bg-gradient-to-r from-white to-blue-20 border-blue-200 shadow-lg",
                isConnected:
                  devices.filter((device) =>
                    device.type.toLowerCase().includes("clear")
                  ).length > 0,
                serialNumber:
                  devices.filter((device) =>
                    device.type.toLowerCase().includes("clear")
                  ).length > 0
                    ? devices.filter((device) =>
                        device.type.toLowerCase().includes("clear")
                      )[0].serialNumber
                    : "",
              },
            ].map((option) => (
              <div
                key={option.key}
                className={`relative overflow-hidden cursor-pointer transition-all duration-300 
                  
                `}
                onClick={() => setSelectedOption(option.key)}
                onMouseEnter={() => setHoveredOption(option.key)}
                onMouseLeave={() => setHoveredOption(null)}
              >
                <div
                  className={`rounded-2xl p-2 border-2 transition-all duration-300 relative ${
                    selectedOption === option.key
                      ? option.gradientClass
                      : hoveredOption === option.key
                      ? option.hoverClass
                      : "bg-white border-gray-200 shadow-sm"
                  }`}
                >
                  {/* Check */}
                  {selectedOption === option.key && (
                    <div className="absolute top-4 right-4">
                      <div
                        className={`check-badge w-8 h-8 rounded-full flex items-center justify-center `}
                      >
                        <IonIcon
                          icon={checkmark}
                          className={`text-white text-lg`}
                          style={
                            option.serialNumber
                              ? { color: "#e5e7eb" }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  )}

                  <div className="flex items-center min-h-[70px]">
                    <div className="w-16 h-16 min-w-[48px] min-h-[50px] rounded-lg flex items-center justify-center mr-4 shadow-sm bg-white">
                      <img
                        src={option.img}
                        alt={
                          typeof option.title === "string"
                            ? option.title
                            : "BiWaze"
                        }
                        className="w-12 h-12 object-contain"
                        style={{ maxWidth: "48px", maxHeight: "48px" }}
                      />
                    </div>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <h4
                        className="font-bold text-xl text-gray-900 mb-1 truncate"
                        style={{ fontFamily: "'Figtree', sans-serif" }}
                      >
                        {option.title}
                      </h4>
                      {option?.serialNumber && (
                        <div className="text-gray-500">
                          SN: {option?.serialNumber}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Continue Button */}
          <div className="button-stack-container flex flex-col">
            <IonButton
              expand="block"
              className="primary-button"
              disabled={!selectedOption}
              style={{ opacity: !selectedOption ? 0.5 : 1 }}
              onClick={() => {
                setUsesBiWazeDevice(
                  selectedOption === "cough"
                    ? BiwazeEnum.COUGH
                    : BiwazeEnum.CLEAR
                );
                handleContinue();
              }}
            >
              Continue
            </IonButton>
            {!hideSkip && (
              <button
                className="skip-link"
                onClick={() => {
                  const step = devices.length === 2 ? 7 : 6;
                  if (
                    devices.length === 0 &&
                    typeof handleNavigateToStep === "function"
                  ) {
                    handleNavigateToStep(STEP_IDS.HEALTH_CONNECT);
                    return;
                  }
                  if (typeof nextStep === "function") {
                    nextStep(step);
                  }
                }}
              >
                Skip for now
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Add IonModal to confirm the override if the use wants to proceed with the Scan if serial number exists for the type  */}
      <IonModal
        isOpen={showOverride}
        onDidDismiss={() => setShowOverride(false)}
        className="connect-modal rounded-2xl max-w-md mx-auto"
        style={{
          backdropFilter: "blur(5px)",
          "--height": "300px",
        }}
      >
        <div
          className="p-6 flex flex-col items-center"
          style={{
            width: "100%",
            maxWidth: "400px",
            height: "auto",
            // maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          {/* Close Icon in the top right */}
          <div className="w-full flex justify-end mb-4">
            <button
              onClick={() => setShowOverride(false)}
              className="text-gray-400"
            >
              <IonIcon icon={close} style={{ fontSize: "24px" }} />
            </button>
          </div>

          {/* Instructions */}
          <h2
            className="text-center mb-8"
            style={{ fontSize: "18px", fontWeight: 500, color: "#333" }}
          >
            {`Scanning a new `}
            {selectedOption === "cough" ? (
              <>
                BiWaze<sup>®</sup> Cough
              </>
            ) : (
              <>
                BiWaze<sup>®</sup>Clear
              </>
            )}
            {` will unlink your previous BiWaze system. Do you want to proceed?`}
          </h2>

          {/* Scan Button */}
          <div className=" flex flex-col gap-3 w-full">
            <IonButton
              expand="block"
              onClick={async () => {
                nextStep();
                setShowOverride(false);
              }}
              className="primary-button "
            >
              Yes
            </IonButton>
            <IonButton
              onClick={() => setShowOverride(false)}
              className="tertiary-button"
            >
              No
            </IonButton>
          </div>
        </div>
      </IonModal>
    </IonContent>
  );
};

export default DeviceUsageConfirm;
